import { useRef } from 'react';
import { NextSeo, OrganizationJsonLd } from 'next-seo';
import { InferGetStaticPropsType, GetStaticProps, GetStaticPaths } from 'next';
import { Option, Supplier, Review, Pagination } from 'src/types';
import pluralize from 'pluralize';
import { BsStarFill } from 'react-icons/bs';
import { HiOutlineArrowNarrowLeft, HiOutlineArrowNarrowRight } from 'react-icons/hi';
import { AppRoutes } from 'src/lib/routes';
import ReadMore from 'src/components/ReadMore';
import ApiClient from 'src/lib/api_client';
import WithDefaultJsonLd from 'src/components/WithDefaultJsonLd';
import ImageWithFallback from 'src/components/ImageWithFallback';
import RevalidateSettings from 'src/lib/revalidate_settings';
import OtherOptions from 'src/components/OtherOptions';
import Slider from 'react-slick';
import { commonCarouselSettings, HEADER_HEIGHT } from 'src/lib/constants';
import styles from 'src/styles/modules/pages/brand.module.scss';
import { FiArrowDown } from 'react-icons/fi';
import useQuery from 'src/hooks/useQuery';
import { ApiRoutes } from 'src/lib/routes';
import { DEFAULT_SORT } from 'src/components/ReviewSectionV2';
import Link from 'next/link';
import { scrollTo } from 'src/helpers';
import { useLocale, useTranslations } from 'next-intl';
import { prepareTranslations } from 'src/lib/prepare_translations';
import useAddLocaleToUrl from 'src/hooks/useAddLocaleToUrl';
import { min } from 'lodash-es';

type QueryData = { data: Review[]; pagination: Pagination };
const Page: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = ({ supplier }) => {
  const translation = useTranslations('pages/operators/[id]')
  const locale = useLocale();
  const addLocaleToUrl = useAddLocaleToUrl()
  const containerOptionsRef = useRef<HTMLDivElement>(null);
  const { data, isLoading } = useQuery<QueryData>(
    ApiRoutes.apiReviewsRoute(
      {},
      {
        reviewable_id: supplier.id,
        reviewable_type: 'Supplier',
        option_id: 0,
        q: {
          body_present: 1,
          supplier_activity_option_id_not_null: 1,
        },
        page: 1,
        per_page: 8,
        sorts: DEFAULT_SORT.split(','),
      }
    ).toUrl()
  );

  const { data: reviews, pagination } = (data || {}) as QueryData;
  const metaTitle = supplier.metaTitle ? supplier.metaTitle : translation('metaTitle', {name: supplier.name});
  const metaDescription = supplier.metaDescription ? supplier.metaDescription : supplier.strippedDescription;

  return (
    <>
      <NextSeo
        title={metaTitle}
        description={metaDescription}
        canonical={addLocaleToUrl(AppRoutes.hostRoute({ id: supplier.slug }).toUrl())}
        openGraph={{
          url: addLocaleToUrl(AppRoutes.hostRoute({ id: supplier.slug }).toUrl()),
          title: metaTitle,
          description: metaDescription,
          images: supplier.ologo?.mdUrl
            ? [
                {
                  url: supplier.ologo?.mdUrl,
                  height: 600,
                  width: 600,
                  alt: supplier.name,
                },
              ]
            : undefined,
        }}
      />
      <WithDefaultJsonLd>
        <OrganizationJsonLd
          name={metaTitle}
          url={AppRoutes.hostRoute({ id: supplier.slug }).toUrl()}
          image={supplier.ologo?.mdUrl || ''}
          description={metaDescription}
          aggregateRating={supplier.ratedReviewsCount > 0 ? {
            ratingValue: supplier.reviewRating,
            reviewCount: supplier.ratedReviewsCount,
          } : undefined}
        />
      </WithDefaultJsonLd>

      <div className="tw-bg-brand-yellow tw-py-16 md:tw-py-24">
        <div className="tw-uppercase tw-font-semibold tw-mb-8 tw-text-center tw-leading-none">
          {translation('meetYourHost')}
        </div>

        <h1 className="tw-text-6xl md:tw-text-8xl tw-leading-none md:tw-leading-none tw-text-center tw-font-styleSans tw-font-semibold tw-mb-8 md:tw-mb-16">
          {supplier.name}
        </h1>

        <div className="container tw-max-w-8xl tw-px-2 md:tw-px-3.75 tw-relative">
          <Slider
            {...commonCarouselSettings}
            slidesToShow={1}
            slidesToScroll={1}
            centerMode={true}
            centerPadding="400px"
            infinite={true}
            arrows={false}
            responsive={[
              {
                breakpoint: 1040,
                settings: {
                  centerPadding: '200px',
                  arrows: false,
                },
              },
              {
                breakpoint: 800,
                settings: {
                  centerPadding: typeof window === 'object' ? `${Math.round((window.innerWidth - 296) / 2)}px` : '67px',
                },
              },
            ]}
            className="tw--mx-2 md:tw--mx-3.75"
          >
            {supplier.optionImages.map((image, index) => (
              <div key={index} className="tw-px-2 md:tw-px-4 tw-outline-none">
                <ImageWithFallback
                  record={image}
                  imgField="webpLgUrl"
                  smImgField="webpMdUrl"
                  alt={image.caption || ''}
                  fill
                  className={`${styles.supplierImage} tw-object-cover tw-block tw-static tw-outline-none`}
                />
              </div>
            ))}
          </Slider>
        </div>

        <div className="tw-max-w-lg tw-mx-auto tw-mt-6 md:tw-mt-8 tw-px-3.75">
          <ReadMore
            mobileLength={120}
            desktopLength={120}
            as="div"
            className="simple-rich-doc tw-text-center"
            fullContent={supplier.description}
            content={supplier.strippedDescription}
            readMoreClass="tw-block tw-text-brand-navy tw-font-semibold tw-underline tw-cursor-pointer tw-mt-8 tw-text-center"
            readMoreText={translation('showMore')}
          />
        </div>

        <div className="tw-text-center tw-mt-6 md:tw-mt-8 tw-px-3.75">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() =>
              containerOptionsRef.current
                ? scrollTo(containerOptionsRef.current, -HEADER_HEIGHT)
                : null
            }
          >
            ↓ {translation('jumpToActivities')} ↓
          </button>
        </div>
      </div>

      {supplier.optionsCount > 0 ? (
        <div className="tw-py-16 md:tw-py-24">
          <div ref={containerOptionsRef}></div>
          <OtherOptions
            record={{
              id: supplier.id,
              type: 'Supplier',
            }}
            isGiftExperience={false}
            renderTitle={() => (
              <div className="tw-text-center tw-font-styleSans tw-font-semibold tw-text-5xl md:tw-text-6xl tw-leading-none md:tw-leading-none tw-mb-8 md:tw-mb-16">
                {translation('discoverAllExperiences')}
              </div>
            )}
            queryParams={{
              per_page: 6,
              sort_by: 'recommended',
            }}
            initialTotalCount={min([supplier.optionsCount, 6])}
          />
        </div>
      ) : null}

      {reviews && reviews.length > 0 ? (
        <div className="tw-bg-brand-navy">
          <div className="container tw-py-16 md:tw-py-24">
            <h2 className="tw-mb-0 tw-text-brand-green tw-font-styleSans tw-text-center tw-font-semibold tw-text-5xl md:tw-text-6xl md:tw-leading-none tw-leading-none">
              {translation('whatOtherGuestsSay')}
            </h2>

            <div className="tw-flex tw-items-center tw-justify-center tw-text-white tw-my-8 md:tw-my-10 tw-leading-none">
              <div className="tw-font-semibold tw-mr-1">
                <span className="tw-leading-none">{supplier.reviewRating}</span>
              </div>
              <BsStarFill size={16} className="tw-text-orange tw-align-middle" />
              <div className="tw-text-white tw-mx-2">|</div>
              <div>{translation('pluralReview', {count: supplier.ratedReviewsCount})}</div>
            </div>

            <Slider
              {...commonCarouselSettings}
              prevArrow={
                <div className="slick-prev">
                  <HiOutlineArrowNarrowLeft
                    size={24}
                    strokeWidth={2}
                    className="tw-text-white hover:tw-text-brand-green"
                  />
                </div>
              }
              nextArrow={
                <div className="slick-next">
                  <HiOutlineArrowNarrowRight
                    size={24}
                    strokeWidth={2}
                    className="tw-text-white hover:tw-text-brand-green"
                  />
                </div>
              }
              slidesToShow={3}
              slidesToScroll={1}
              responsive={[
                {
                  breakpoint: 800,
                  settings: {
                    slidesToShow: 2,
                  },
                },
                {
                  breakpoint: 540,
                  settings: {
                    slidesToShow: 1,
                  },
                },
              ]}
              className="tw--mx-3.75 slider-v2"
            >
              {reviews.map((review, index) => (
                <div className="tw-px-3.75" key={index}>
                  <div className="tw-p-8 tw-bg-brand-navy tw-rounded-lg tw-border tw-border-brand-green tw-text-center">
                    <div className="tw-font-styleSans tw-text-white tw-font-semibold tw-text-lg md:tw-text-2xl tw-leading-none md:tw-leading-none tw-h-28 md:tw-h-32 tw-mb-2">
                      <ReadMore
                        mobileLength={140}
                        desktopLength={108}
                        as="div"
                        className=""
                        content={review.strippedBody || ''}
                        dotOnly={true}
                      />
                    </div>
                    <div className="tw-text-sm tw-text-white tw-font-semibold tw-text-center tw-mb-4 md:tw-mb-8 tw-leading-none">
                      {review.reviewer}
                    </div>
                    {review.option ? (
                      <div className="tw-h-10">
                        <span className="tw-font-semibold tw-text-white tw-text-sm">{translation('booked')}</span>{' '}
                        <Link legacyBehavior prefetch={false} href={AppRoutes.experienceRoute({ id: review.option.slugCode }).toPath()}>
                          <a className="tw-text-white tw-underline tw-text-sm">{review.option.name}</a>
                        </Link>
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </Slider>
            <div className="tw-pb-10"></div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<{
  supplier: Supplier;
}> = async (context) => {
  if (!context.params?.id) {
    return {
      notFound: true,
      revalidate: RevalidateSettings.veryLong,
    };
  }

  const translations = await prepareTranslations(context, ['pages/operators/[id]']);
  const [supplier] = await ApiClient.getSupplier(
    { id: String(context.params.id) },
    { 'X-NEXT-LOCALE': translations.locale }
  );

  if ('error' in supplier) {
    return {
      notFound: true,
      revalidate: RevalidateSettings.veryLong,
    };
  }

  return {
    props: {
      supplier,
      ...translations
    },
    revalidate: RevalidateSettings.veryLong,
  };
};

export default Page;
